import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CommunitySection = () => {
  const [selectedEvent, setSelectedEvent] = useState(0);

  const upcomingEvents = [
    {
      id: 1,
      title: "Mediterranean Cooking Class",
      subtitle: "Learn to Make Authentic Kebabs",
      date: "March 15, 2024",
      time: "6:00 PM - 8:30 PM",
      price: "$45 per person",
      description: "Join Chef Omar for an hands-on cooking experience where you\'ll learn the secrets of authentic Mediterranean kebab preparation, from spice blending to grilling techniques.",
      image: "https://images.pexels.com/photos/4253302/pexels-photo-4253302.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      instructor: "Chef Omar",
      capacity: "12 people",
      spotsLeft: 4,
      includes: ["All ingredients", "Recipe cards", "Dinner & drinks", "Take-home spices"],
      difficulty: "Beginner Friendly",
      category: "Cooking Class"
    },
    {
      id: 2,
      title: "Wine & Mezze Pairing Night",
      subtitle: "Mediterranean Wines Meet Traditional Appetizers",
      date: "March 22, 2024",
      time: "7:00 PM - 9:00 PM",
      price: "$35 per person",
      description: "Discover the perfect harmony between Mediterranean wines and our signature mezze selection. Learn about wine regions, tasting notes, and traditional pairing principles.",
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      instructor: "Sommelier Maria",
      capacity: "20 people",
      spotsLeft: 8,
      includes: ["5 wine tastings", "Mezze platter", "Pairing guide", "10% off wine purchases"],
      difficulty: "All Levels",
      category: "Wine Tasting"
    },
    {
      id: 3,
      title: "Family Recipe Sharing Circle",
      subtitle: "Stories & Traditions from Our Community",
      date: "March 29, 2024",
      time: "5:00 PM - 7:00 PM",
      price: "Free Event",
      description: "A heartwarming evening where community members share their family recipes and the stories behind them. Enjoy samples, make connections, and celebrate our diverse culinary heritage.",
      image: "https://images.pexels.com/photos/5474640/pexels-photo-5474640.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      instructor: "Community Led",
      capacity: "30 people",
      spotsLeft: 15,
      includes: ["Recipe samples", "Community cookbook", "Tea & coffee", "Recipe exchange"],
      difficulty: "All Welcome",
      category: "Community Event"
    }
  ];

  const communityHighlights = [
    {
      id: 1,
      type: "Customer Spotlight",
      title: "Sarah\'s Mediterranean Journey",
      description: "How our cooking classes inspired a home chef to explore her heritage",
      image: "https://randomuser.me/api/portraits/women/32.jpg",
      author: "Sarah Johnson",
      date: "2 weeks ago",
      likes: 47,
      comments: 12
    },
    {
      id: 2,
      type: "Recipe Share",
      title: "Grandma\'s Secret Hummus Recipe",
      description: "A family recipe shared by one of our beloved regular customers",
      image: "https://images.pexels.com/photos/6107787/pexels-photo-6107787.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
      author: "Elena Vasquez",
      date: "1 week ago",
      likes: 89,
      comments: 23
    },
    {
      id: 3,
      type: "Community Impact",
      title: "Local Farmers Partnership",
      description: "Supporting local agriculture while bringing you the freshest ingredients",
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
      author: "Kebab Palace Team",
      date: "3 days ago",
      likes: 156,
      comments: 34
    }
  ];

  const cookingTips = [
    {
      id: 1,
      tip: "Perfect Kebab Marinade",
      description: "The secret is in the yogurt - it tenderizes the meat while adding Mediterranean tang",
      chef: "Chef Omar",
      difficulty: "Easy",
      time: "2 hours marinating"
    },
    {
      id: 2,
      tip: "Fluffy Pita Bread",
      description: "High heat and steam are key to achieving that perfect pocket every time",
      chef: "Chef Hassan",
      difficulty: "Medium",
      time: "45 minutes"
    },
    {
      id: 3,
      tip: "Creamy Hummus Secret",
      description: "Remove the chickpea skins and add ice water while blending for ultra-smooth texture",
      chef: "Chef Layla",
      difficulty: "Easy",
      time: "15 minutes"
    }
  ];

  const nextEvent = () => {
    setSelectedEvent((prev) => (prev + 1) % upcomingEvents.length);
  };

  const prevEvent = () => {
    setSelectedEvent((prev) => (prev - 1 + upcomingEvents.length) % upcomingEvents.length);
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Join Our Community
          </h2>
          <p className="font-body text-xl text-text-secondary max-w-3xl mx-auto">
            More than a restaurant - we're a gathering place for food lovers, culture enthusiasts, and anyone who believes that the best meals are shared with others
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Featured Event */}
          <div className="lg:col-span-2">
            <div className="bg-surface rounded-2xl shadow-warm-lg overflow-hidden">
              {/* Event Header */}
              <div className="p-6 border-b border-primary-100">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 bg-accent text-white rounded-full text-sm font-cta font-semibold">
                    {upcomingEvents[selectedEvent].category}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={prevEvent}
                      className="w-8 h-8 bg-primary-100 text-primary rounded-full flex items-center justify-center hover:bg-primary-200 transition-organic"
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </button>
                    <button
                      onClick={nextEvent}
                      className="w-8 h-8 bg-primary-100 text-primary rounded-full flex items-center justify-center hover:bg-primary-200 transition-organic"
                    >
                      <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                </div>
                
                <h3 className="font-headline text-2xl lg:text-3xl font-bold text-text-primary mb-2">
                  {upcomingEvents[selectedEvent].title}
                </h3>
                <h4 className="font-accent text-lg text-text-secondary mb-4">
                  {upcomingEvents[selectedEvent].subtitle}
                </h4>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={16} className="text-primary" />
                    <span>{upcomingEvents[selectedEvent].date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} className="text-primary" />
                    <span>{upcomingEvents[selectedEvent].time}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="DollarSign" size={16} className="text-primary" />
                    <span>{upcomingEvents[selectedEvent].price}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Users" size={16} className="text-primary" />
                    <span>{upcomingEvents[selectedEvent].spotsLeft} spots left</span>
                  </div>
                </div>
              </div>

              {/* Event Image */}
              <div className="relative h-64 lg:h-80">
                <Image
                  src={upcomingEvents[selectedEvent].image}
                  alt={upcomingEvents[selectedEvent].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-text-primary/60 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="ChefHat" size={20} className="text-white" />
                      </div>
                      <div>
                        <div className="font-cta font-semibold text-text-primary">
                          {upcomingEvents[selectedEvent].instructor}
                        </div>
                        <div className="text-sm text-text-secondary">
                          {upcomingEvents[selectedEvent].difficulty}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div className="p-6">
                <p className="font-body text-text-secondary leading-relaxed mb-6">
                  {upcomingEvents[selectedEvent].description}
                </p>
                
                <div className="mb-6">
                  <h5 className="font-cta font-semibold text-text-primary mb-3">What's Included:</h5>
                  <div className="grid grid-cols-2 gap-2">
                    {upcomingEvents[selectedEvent].includes.map((item, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Icon name="Check" size={16} className="text-success" />
                        <span className="text-sm text-text-secondary">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button className="btn-conversion flex-1">
                    <Icon name="Calendar" size={20} className="mr-2" />
                    Reserve Your Spot
                  </button>
                  <button className="btn-primary flex-1">
                    <Icon name="Info" size={20} className="mr-2" />
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Content */}
          <div className="space-y-6">
            {/* Community Highlights */}
            <div className="bg-surface rounded-lg p-6 shadow-warm">
              <h3 className="font-cta font-bold text-lg text-text-primary mb-4 flex items-center">
                <Icon name="Heart" size={20} className="mr-2 text-primary" />
                Community Highlights
              </h3>
              
              <div className="space-y-4">
                {communityHighlights.map((highlight) => (
                  <div key={highlight.id} className="border-b border-primary-100 last:border-b-0 pb-4 last:pb-0">
                    <div className="flex items-start space-x-3">
                      <Image
                        src={highlight.image}
                        alt={highlight.author}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="px-2 py-1 bg-trust-100 text-trust-700 rounded-full text-xs font-medium">
                            {highlight.type}
                          </span>
                        </div>
                        <h4 className="font-cta font-semibold text-sm text-text-primary mb-1">
                          {highlight.title}
                        </h4>
                        <p className="text-xs text-text-secondary mb-2 line-clamp-2">
                          {highlight.description}
                        </p>
                        <div className="flex items-center justify-between text-xs text-text-secondary">
                          <span>by {highlight.author}</span>
                          <div className="flex items-center space-x-3">
                            <span className="flex items-center space-x-1">
                              <Icon name="Heart" size={12} />
                              <span>{highlight.likes}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Icon name="MessageCircle" size={12} />
                              <span>{highlight.comments}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button className="w-full mt-4 text-primary hover:text-primary-600 font-cta font-semibold text-sm transition-organic">
                View All Stories
              </button>
            </div>

            {/* Cooking Tips */}
            <div className="bg-surface rounded-lg p-6 shadow-warm">
              <h3 className="font-cta font-bold text-lg text-text-primary mb-4 flex items-center">
                <Icon name="Lightbulb" size={20} className="mr-2 text-accent" />
                Chef's Tips
              </h3>
              
              <div className="space-y-4">
                {cookingTips.map((tip) => (
                  <div key={tip.id} className="border-b border-primary-100 last:border-b-0 pb-4 last:pb-0">
                    <h4 className="font-cta font-semibold text-sm text-text-primary mb-2">
                      {tip.tip}
                    </h4>
                    <p className="text-xs text-text-secondary mb-2">
                      {tip.description}
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-primary font-medium">{tip.chef}</span>
                      <div className="flex items-center space-x-2 text-text-secondary">
                        <span>{tip.difficulty}</span>
                        <span>•</span>
                        <span>{tip.time}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button className="w-full mt-4 text-primary hover:text-primary-600 font-cta font-semibold text-sm transition-organic">
                More Cooking Tips
              </button>
            </div>

            {/* Newsletter Signup */}
            <div className="bg-gradient-to-br from-primary to-primary-600 rounded-lg p-6 text-white">
              <h3 className="font-cta font-bold text-lg mb-3 flex items-center">
                <Icon name="Mail" size={20} className="mr-2" />
                Stay Connected
              </h3>
              <p className="text-white/90 text-sm mb-4">
                Get the latest on events, recipes, and special offers delivered to your inbox
              </p>
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="w-full px-4 py-2 rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <button className="w-full bg-white text-primary font-cta font-semibold py-2 rounded-lg hover:bg-primary-50 transition-organic">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="max-w-2xl mx-auto">
            <h3 className="font-headline text-2xl font-bold text-text-primary mb-4">
              Ready to Join Our Mediterranean Family?
            </h3>
            <p className="font-body text-text-secondary mb-6">
              Whether you're looking to learn new skills, meet fellow food lovers, or simply enjoy great company, there's a place for you at our table.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="btn-conversion">
                <Icon name="Calendar" size={20} className="mr-2" />
                View All Events
              </button>
              <Link
                to="/contact-location-welcoming-connection"
                className="btn-primary text-center"
              >
                <Icon name="MessageCircle" size={20} className="mr-2" />
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunitySection;